import { Injectable, OnInit } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { onBoardingService } from './onBoarding.service';
import { catchError, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RouteGuardGuard implements CanActivate, OnInit {
  private stepSubject = new BehaviorSubject<number>(1);
  private data: any = { currentStep: 1 }; // Valor por defecto
  private isLoading = false;
  private lastUserGUID: string | null = null;

  constructor(private router: Router,
              private _onBoardingService: onBoardingService) {
  }

  ngOnInit(): void {
    // Cargar datos solo una vez y solo si no se han cargado por AppComponent
    if (this.stepSubject.getValue() === 1) {
      this.loadData();
    }
  }

  private loadData(): void {
    // Evitar múltiples peticiones simultáneas
    if (this.isLoading) {
      return;
    }
    
    this.isLoading = true;
    
    this._onBoardingService.List()
      .pipe(
        catchError(error => {
          console.error('Error al cargar datos en RouteGuard:', error);
          this.isLoading = false;
          // En caso de error, mantener el paso por defecto
          return of(this.data);
        })
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          
          if (response) {
            // Comprobar si ha cambiado el usuario
            const currentUserGUID = this._onBoardingService.getUserGUID();
            if (this.lastUserGUID !== currentUserGUID) {
              // Si ha cambiado el usuario, forzamos la actualización del step
              this.lastUserGUID = currentUserGUID;
            }
            
            this.data = response;
            // Si hay un currentStep guardado, actualizar el BehaviorSubject
            if (this.data && this.data.currentStep) {
              this.stepSubject.next(this.data.currentStep);
            }
          }
        },
        error: (err) => {
          // Este bloque no debería ejecutarse gracias al catchError,
          // pero lo mantenemos como precaución adicional
          console.error('Error inesperado en la suscripción:', err);
          this.isLoading = false;
        }
      });
  }

  getStep(): Observable<number> {
    return this.stepSubject.asObservable();
  }

  updateStep(updateStepNumber: number): void {
    
    // Always update the step subject immediately, especially for step 0
    // console.log("Actualizando paso a:", updateStepNumber);
    
    // Immediately emit the new step value to all subscribers
    this.stepSubject.next(updateStepNumber);
    
    // Only proceed with server update if the step is higher or it's step 0
    if (this.stepSubject.getValue() < updateStepNumber || updateStepNumber === 0) {
      this._onBoardingService.List()
        .pipe(
          timeout(3000),
          catchError(error => {
            console.error('Error al cargar datos para actualizar paso:', error);
            // Si no podemos cargar, usamos los datos actuales o creamos un objeto nuevo
            return of(this.data || {});
          })
        )
        .subscribe({
          next: (response) => {
            // Usamos los datos más recientes del servidor
            const updatedData = response || {};
            // Actualizamos solo el currentStep            
            updatedData.currentStep = updateStepNumber;
            
            // Guardamos los datos actualizados
            this._onBoardingService.Save(updatedData)
              .pipe(
                timeout(3000),
                catchError(error => {
                  console.error('Error al guardar el paso:', error);
                  return of(null);
                })
              )
              .subscribe({
                next: (response) => {
                  // Actualizamos el objeto data local con los datos más recientes
                  this.data = updatedData;
                },
                error: (err) => {
                  console.error('Error inesperado al guardar el paso:', err);
                }
              });
          },
          error: (err) => {
            console.error('Error al obtener datos actuales antes de guardar:', err);
          }
        });
    }
  }

  // Llamar este método cuando recibas el JSON del API
  initializeStepFromApiData(data: any): void {
    const step = data ?? 1;
    this.stepSubject.next(step);
    
    // Actualizar el GUID del usuario actual
    this.lastUserGUID = this._onBoardingService.getUserGUID();
  }

  // Método para forzar una recarga del paso actual del usuario
  refreshUserStep(): void {
    // Reiniciar el estado de carga
    this.isLoading = false;
    // Forzar la recarga de datos del usuario actual
    this.loadData();
  }

  goToStepPage(): void {
    const currentStep = this.stepSubject.getValue();
    switch (currentStep) {
      case 2:
        this.router.navigate(['/onboarding/project-info']);
        break;
      case 3:
        this.router.navigate(['/onboarding/general-preferences']);
        break;
      case 4:
        this.router.navigate(['/onboarding/modules']);
        break;
      case 5:
        this.router.navigate(['/onboarding/ranking-options']);
        break;
      case 6:
        this.router.navigate(['/onboarding/incentive-campaing']);
        break;
      case 7:
        this.router.navigate(['/onboarding/internal-communication']);
        break;
      case 8:
        this.router.navigate(['/onboarding/invitation-options']);
        break;
      case 9:
        this.router.navigate(['/onboarding/ending']);
        break;
      default:
        this.router.navigate(['/onboarding/welcome']);
        break;
    }
  }

  // Método para verificar si el usuario puede activar una ruta
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    try {
      const path = route.routeConfig?.path;
      const currentStep = this.stepSubject.getValue();

      // Verifica si el usuario tiene acceso a la ruta solicitada
      const stepPermissions: { [key: string]: number } = {
        'welcome': 1,
        'project-info': 2,
        'general-preferences': 3,
        'modules': 4,
        'ranking-options': 5,
        'incentive-campaing': 6,
        'internal-communication': 7,
        'invitation-options': 8,
        'ending': 9,
      };

      if (path && stepPermissions[path] && currentStep >= stepPermissions[path]) {
        return true;
      }

      if (currentStep === 0) {
        this.updateStep(1); 
        this.router.navigate(['/onboarding/welcome']);
        return false;
      }

      console.warn('Acceso denegado, redirigiendo a la página de bienvenida.');
      this.router.navigate(['/onboarding/welcome']);
      return false;
    } catch (error) {
      console.error('Error en canActivate:', error);
      // En caso de error, permitir la navegación y redirigir a welcome
      this.router.navigate(['/onboarding/welcome']);
      return false;
    }
  }
}